import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Award, MessageSquare, ThumbsUp, CheckCircle2 } from "lucide-react";
import { getForumMember } from "@/lib/forum.functions";

export const Route = createFileRoute("/forum/medlem/$id")({
  head: () => ({
    meta: [
      { title: "Medlemsprofil – Fritidsforum | AK Husbil & Fritid" },
      {
        name: "description",
        content:
          "Medlemsprofil i Fritidsforum: karma, bästa svar, trådar och märken för medlemmar i vårt community för husbil och husvagn.",
      },
      { property: "og:title", content: "Medlemsprofil – Fritidsforum" },
      {
        property: "og:description",
        content: "Karma, bästa svar och trådar från en medlem i Fritidsforum.",
      },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: MemberPage,
  errorComponent: () => (
    <div className="container-edge py-20">
      <h1 className="font-display text-3xl">Kunde inte visa medlemmen</h1>
      <Link to="/forum" className="mt-4 inline-block text-accent hover:underline">
        Tillbaka till forumet
      </Link>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-edge py-20">
      <h1 className="font-display text-3xl">Medlemmen finns inte</h1>
    </div>
  ),
});

function MemberPage() {
  const { id } = Route.useParams();
  const fetchMember = useServerFn(getForumMember);
  const { data, isLoading } = useQuery({
    queryKey: ["forum", "member", id],
    queryFn: () => fetchMember({ data: { id } }),
  });

  if (isLoading) {
    return <p className="container-edge py-20 text-sm text-muted-foreground">Laddar profil…</p>;
  }
  if (!data) return null;

  const { member, stats, badges, posts } = data;
  const name = member.display_name || "Medlem";

  return (
    <section className="bg-warm">
      <div className="container-edge py-12 md:py-16">
        <p className="eyebrow">Fritidsforum · Medlem</p>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          {member.avatar_url ? (
            <img
              src={member.avatar_url}
              alt={`Profilbild för ${name}`}
              className="h-16 w-16 rounded-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="grid h-16 w-16 place-items-center rounded-full bg-secondary font-display text-xl">
              {name.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-display text-3xl sm:text-4xl">{name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {stats.karma} karma · {stats.posts} trådar · {stats.comments} svar
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={ThumbsUp} label="Karma" value={stats.karma} />
          <Stat icon={MessageSquare} label="Trådar" value={stats.posts} />
          <Stat icon={MessageSquare} label="Svar" value={stats.comments} />
          <Stat icon={CheckCircle2} label="Bästa svar" value={stats.bestAnswers} />
        </div>

        {badges.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-2xl">Märken</h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {badges.map((b) => (
                <li
                  key={b.label}
                  title={b.hint}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold"
                >
                  <Award size={13} className="text-accent" /> {b.label}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-10">
          <h2 className="font-display text-2xl">Senaste trådar</h2>
          <ul className="mt-4 space-y-3">
            {posts.map((p) => (
              <li key={p.id} className="rounded-2xl border border-border bg-card p-4">
                <Link
                  to="/forum/t/$slug"
                  params={{ slug: p.slug }}
                  className="font-display text-lg transition hover:text-accent"
                >
                  {p.title}
                </Link>
                <p className="mt-1 text-xs text-muted-foreground">
                  {p.vote_score} röster · {p.comment_count} svar ·{" "}
                  {new Date(p.created_at).toLocaleDateString("sv-SE")}
                </p>
              </li>
            ))}
            {!posts.length && (
              <li className="text-sm text-muted-foreground">Inga trådar ännu.</li>
            )}
          </ul>
        </div>

        <Link to="/forum" className="mt-10 inline-block text-sm text-accent hover:underline">
          ← Till Fritidsforum
        </Link>
      </div>
    </section>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof ThumbsUp;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon size={12} /> {label}
      </p>
      <p className="mt-1 font-display text-2xl tabular-nums">{value}</p>
    </div>
  );
}
